import { Box } from "@mui/material";

/**
 * Parses a multiline markdown table into a 2D string array.
 * Handles the standard pipe-table format produced by the AI:
 *
 *   |   |   |   |   |
 *   |---|---|---|---|
 *   |   | 3 |   |   |
 *   | 6 | ? | 6 | 7 |
 *   |   | 9 |   |   |
 *
 * Also handles compact single-line tables where rows are separated
 * by  "|  |" (pipe + multiple spaces + pipe) boundaries.
 *
 * Returns null when no valid table is found.
 */
const parseMdTableGrid = (text) => {
  if (!text || !text.includes("|")) return null;

  // Normalise: expand compact single-line tables → multiline
  let src = text.trim();
  if (!src.includes("\n") && /\|[-:]{2,}\|/.test(src)) {
    // Split rows at "| +" immediately followed by another "|"
    // that is NOT a separator dash token.
    const segments = src.split(/\|\s{2,}(?=\|)/);
    if (segments.length > 1) {
      src = segments.map((s, i) => (i < segments.length - 1 ? s + "|" : s)).join("\n");
    }
  }

  const lines = src
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("|"));

  if (lines.length < 2) return null;

  const isSep = (l) =>
    /^\|[\s\-:|]+\|$/.test(l) || /^\|(\s*-{2,}\s*\|)+$/.test(l);

  const sepIdx = lines.findIndex(isSep);
  if (sepIdx < 0) return null;

  const parseRow = (line) =>
    line
      .split("|")
      .filter((_, j, arr) => j > 0 && j < arr.length - 1)
      .map((c) => c.trim());

  // Number of columns comes from the separator row
  const sepCells = parseRow(lines[sepIdx]);
  const N = sepCells.length > 0 ? sepCells.length : parseRow(lines[0]).length;
  if (N === 0) return null;

  const dataLines = lines.slice(sepIdx + 1).filter((l) => !isSep(l) && /\|/.test(l));
  if (dataLines.length === 0) return null;

  return dataLines.map((l) => {
    const cells = parseRow(l);
    while (cells.length < N) cells.push("");
    return cells.slice(0, N);
  });
};

/**
 * Checks whether a parsed grid has a sparse "cross" pattern
 * (at least one row with mostly empty cells flanked by a full row).
 * Used to confirm this is really a cube net and not a data table.
 */
const isCrossGrid = (grid) => {
  if (!grid || grid.length < 2) return false;
  const N = Math.max(...grid.map((r) => r.length));
  // At least one row must be "full" (no empty cells)
  const hasFullRow = grid.some((r) => r.every((c) => c !== ""));
  // At least one row must be "sparse" (some empty cells)
  const hasSparseRow = grid.some((r) => r.some((c) => c === "") && r.some((c) => c !== ""));
  return hasFullRow && hasSparseRow && N >= 3;
};

/**
 * Renders a cube net (unfolded cube / cross-shaped grid) from a markdown
 * table stored in image_description.
 *
 * Cells with content get a visible bordered box.
 * Empty cells are rendered as transparent spacers so the cross shape
 * appears naturally — matching the original exam paper layout.
 *
 * Props:
 *   imageDescription  string   – markdown table text from the question
 *   label             string   – optional override for the "DIAGRAM" header label
 */
const DiagramCubeNet = ({ imageDescription, label }) => {
  const grid = parseMdTableGrid(imageDescription);

  if (!grid || !isCrossGrid(grid)) return null;

  const numCols = Math.max(...grid.map((r) => r.length));
  const CELL = 58; // px – matches typical exam paper cell size

  return (
    <Box sx={{ mb: 2 }}>
      {/* Grid — no header label; the cross-shape is self-evident like on the paper */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          pl: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {grid.map((row, ri) => (
            <Box key={ri} sx={{ display: "flex" }}>
              {Array.from({ length: numCols }, (_, ci) => {
                const cell = row[ci] ?? "";
                const isEmpty = cell === "";
                const isQuestion = cell === "?" || cell === "□" || cell === "_";

                return (
                  <Box
                    key={ci}
                    sx={{
                      width: CELL,
                      height: CELL,
                      border: isEmpty ? "none" : "2px solid #333",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      fontFamily: "'Georgia', serif",
                      bgcolor: isEmpty
                        ? "transparent"
                        : isQuestion
                        ? "primary.50"
                        : "#fff",
                      color: isQuestion ? "primary.700" : "text.primary",
                      visibility: isEmpty ? "hidden" : "visible",
                      userSelect: "none",
                    }}
                  >
                    {!isEmpty && cell}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export { parseMdTableGrid, isCrossGrid };
export default DiagramCubeNet;
